import type { ExplorerGroup } from "@/shared/components/evidence-explorer";
import { specificPatternStats } from "./pattern-dossier";
import type {
  DevelopmentArea,
  PatternExample,
  PatternStat,
  PatternStatsResponse,
  PrinciplePatternStat,
} from "./patterns.api";

export type PatternExplorerItem =
  | { id: string; kind: "tactical"; pattern: PatternStat }
  | { id: string; kind: "principle"; pattern: PrinciplePatternStat }
  | { id: string; kind: "moment"; title: string; note: string; examples: PatternExample[] };

export const principleItemId = (principle: string) => `principle:${principle}`;

const WATCHED = new Set(["accepted", "pending", "inconclusive"]);

const RANGES = { opening: "moves 1–10", middlegame: "moves 11–30", late: "moves 31+" } as const;
type Range = keyof typeof RANGES;

const positioned = (examples: readonly (PatternExample | null | undefined)[] | undefined) =>
  (examples ?? []).filter((example): example is PatternExample => Boolean(example?.fenBefore));

/** Items whose saved positions back a summary tile; items without a position are left out. */
const momentItems = (
  stats: Partial<
    Pick<
      PatternStatsResponse,
      "missedOpportunityExample" | "steadyReplyExample" | "insightExamples" | "stageErrorCounts" | "clockHabits"
    >
  >,
): PatternExplorerItem[] => {
  const counts = stats.stageErrorCounts;
  const heaviest = counts
    ? (Object.keys(RANGES) as Range[]).reduce((best, range) => (counts[range] > counts[best] ? range : best))
    : null;
  const clock = stats.clockHabits;
  const items: PatternExplorerItem[] = [
    {
      id: "moment:missed_opportunity",
      kind: "moment",
      title: "Missed chance",
      note: "Your costly move right after an opponent blunder.",
      examples: positioned([stats.missedOpportunityExample]),
    },
    {
      id: "moment:steady_reply",
      kind: "moment",
      title: "Steady reply",
      note: "You answered an opponent blunder without a costly move.",
      examples: positioned([stats.steadyReplyExample]),
    },
    ...(heaviest
      ? [
          {
            id: `insight:phase_${heaviest}`,
            kind: "moment" as const,
            title: `Errors in ${RANGES[heaviest]}`,
            note: "Your costliest moves in the move range where most errors happen.",
            examples: positioned(stats.insightExamples?.phase[heaviest]),
          },
        ]
      : []),
    ...(clock
      ? [
          {
            id: "insight:fast",
            kind: "moment" as const,
            title: `Fast moves (≤${clock.rushedSeconds}s)`,
            note: "Costly moves played quickly.",
            examples: positioned(stats.insightExamples?.fast),
          },
          {
            id: "insight:low_clock",
            kind: "moment" as const,
            title: `Low clock (≤${clock.timePressureSeconds}s left)`,
            note: "Costly moves played low on time.",
            examples: positioned(stats.insightExamples?.lowClock),
          },
        ]
      : []),
  ];
  return items.filter((item) => item.kind === "moment" && item.examples.length > 0);
};

export interface PatternExplorer {
  groups: ExplorerGroup<PatternExplorerItem>[];
  defaultId: string | null;
}

export const buildPatternExplorer = (
  stats: Pick<PatternStatsResponse, "missedTactics" | "principleSignals" | "developmentAreas" | "focus"> &
    Partial<
      Pick<
        PatternStatsResponse,
        "missedOpportunityExample" | "steadyReplyExample" | "insightExamples" | "stageErrorCounts" | "clockHabits"
      >
    >,
  area: DevelopmentArea["key"] | null,
): PatternExplorer => {
  const selectedArea = stats.developmentAreas?.find((item) => item.key === area) ?? null;
  const tactical: PatternExplorerItem[] = specificPatternStats(stats.missedTactics)
    .filter((pattern) => !selectedArea || selectedArea.patterns.includes(pattern.motif))
    .map((pattern) => ({ id: pattern.motif, kind: "tactical", pattern }));
  // Skill areas group tactical motifs only, so a chosen area shows no principles.
  const principles: PatternExplorerItem[] = selectedArea
    ? []
    : (stats.principleSignals ?? []).map((pattern) => ({
        id: principleItemId(pattern.principle),
        kind: "principle",
        pattern,
      }));
  // Moments are positions behind summary measurements; like principles they sit outside skill areas.
  const moments = selectedArea ? [] : momentItems(stats);
  const focus = stats.focus?.focus;
  const focusId =
    focus?.type === "tactical" &&
    focus.key &&
    WATCHED.has(focus.status) &&
    tactical.some((item) => item.id === focus.key)
      ? focus.key
      : null;
  return {
    groups: [
      { id: "tactical", label: "Tactical patterns", items: tactical },
      { id: "principle", label: "Principles", items: principles },
      { id: "moments", label: "Moments", items: moments },
    ],
    defaultId: focusId ?? tactical[0]?.id ?? principles[0]?.id ?? null,
  };
};

/** Phones open to the list; wide screens always show an item. */
export const resolvePatternSelection = (
  explorer: PatternExplorer,
  requested: string | null,
  compact: boolean,
): string | null => {
  const known = explorer.groups.some((group) => group.items.some((item) => item.id === requested));
  if (requested && known) return requested;
  return compact ? null : explorer.defaultId;
};
